import { Autocomplete, TextField } from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Tag } from "services/KnowfullyAdminClient";

const noTags: Tag[] = [];

type SelectTagMultipleProps = {
  organizationId?: string;
  value: string[];
  onChange: (ids: string[]) => void;
};

// Offers the tags on the SKUs the header organization sees in the list.
const SelectTagMultiple = ({ organizationId, value, onChange }: SelectTagMultipleProps) => {
  const { skuService } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const { data, isFetching, isError } = useQuery(
    ["skuService.getAvailableTags", organizationId],
    () => skuService.getAvailableTags(),
    { enabled: !!organizationId }
  );

  // Nothing is offered when the lookup fails, so the selection is dropped instead of filtering unseen.
  const tags = isError ? noTags : data?.data;

  // A selection the options no longer hold would keep filtering the list without a chip.
  useEffect(() => {
    if (!tags) return;
    const available = value.filter((id) => tags.some((tag) => tag.id === id));
    if (available.length !== value.length) onChange(available);
  }, [tags, value, onChange]);

  return (
    <Autocomplete
      multiple
      options={tags ?? []}
      getOptionLabel={(tag: Tag) => tag.name ?? "?"}
      value={(tags ?? []).filter((tag) => value.includes(tag.id!))}
      onChange={(_, selected: Tag[]) => onChange(selected.map((tag) => tag.id!))}
      loading={isFetching}
      ChipProps={{ size: "small" }}
      renderInput={(params) => <TextField {...params} variant="outlined" label={t({ id: "tags" })} />}
    />
  );
};

export default SelectTagMultiple;
